/* eslint-disable no-warning-comments */

import classNames from 'classnames'
import {gsap} from 'gsap'
import * as React from 'react'

import {makeEditable, storyToLink} from '@/common/utils'
import {Grid} from '@/components/Grid'
import Image from '@/components/Image'
import Link from '@/components/Link'

const formatDate = (date) => {
  if (date) {
    return new Date(date).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }
}

const FeaturedPage = ({backgroundColors, page = {}, buttonLabel, _editable}) => {
  const {title, description, date, cover, category} = page.content || {}
  const sectionRef = React.useRef()

  React.useEffect(() => {
    const section = sectionRef.current
    if (section) {
      const context = gsap.context(() => {
        gsap.fromTo(
          'path',
          {drawSVG: '0%'},
          {
            drawSVG: '100%',
            scrollTrigger: {
              trigger: section,
              start: 'center bottom',
            },
          }
        )
      }, section)
      return () => context.revert()
    }
  })

  return (
    <section ref={sectionRef} {...makeEditable(_editable)} className='py-16 '>
      <Grid>
        <div
          className={classNames(
            'group relative col-span-full overflow-hidden rounded-2xl lg:flex',
            backgroundColors.current
          )}
        >
          <Link
            link={storyToLink(page)}
            className='block w-full p-6 md:flex md:flex-col md:p-8 lg:flex lg:flex-row lg:gap-8 lg:px-18 lg:py-16 lg:pr-26'
          >
            <DoodleArrow
              aria-hidden
              className='absolute top-0 hidden md:left-3/4 md:block lg:left-[43%]'
            />
            <DoodleOutline
              aria-hidden
              className='absolute hidden md:top-1/4 md:block lg:top-[47%] lg:left-0'
            />
            <div className='col-span-full mb-6 md:mb-8 lg:col-span-5 lg:mb-0 lg:w-1/2'>
              <div className='aspect-w-4 aspect-h-3'>
                <div className='overflow-hidden rounded-2xl'>
                  <Image
                    {...cover}
                    className='rounded-2xl transition group-hover:scale-110'
                    ratio='4:3'
                    sizes={{md: 50, lg: 33}}
                  />
                </div>
              </div>
            </div>
            <div className='relative z-10 col-span-full lg:col-span-5 lg:flex lg:flex-1 lg:flex-col lg:justify-between'>
              <div>
                <p className='heading-s-millik text-purple-900'>{title}</p>
                <div className='misc-button-s py-2'>
                  <div className='misc-button-s flex items-center gap-4'>
                    {!!category && (
                      <span className='rounded-full border border-purple-900 px-3 py-1 '>
                        {category.name}
                      </span>
                    )}
                    {category && date && <span>•</span>}
                    {!!date && <span>{formatDate(date)}</span>}
                  </div>
                </div>
                <p className='body-s-regular line-clamp-3'>{description}</p>
              </div>

              <div className='misc-button-m mt-4 rounded-full border border-purple-900 py-4 text-center transition group-hover:bg-purple-900 group-hover:text-white'>
                {buttonLabel}
              </div>
            </div>
          </Link>
        </div>
      </Grid>
    </section>
  )
}

const DoodleArrow = (props) => (
  <svg
    width='235'
    height='194'
    viewBox='0 0 235 194'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    {...props}
  >
    <g clipPath='url(#clip0_1323_37752)'>
      <path
        d='M227.912 -17.3976C228.265 -17.8221 228.895 -17.88 229.32 -17.5268C229.745 -17.1735 229.802 -16.543 229.449 -16.1185L227.912 -17.3976ZM71.6127 -11.8239L72.5399 -11.4494L71.6127 -11.8239ZM2.88545 176.024C2.54017 175.593 2.6097 174.964 3.04074 174.619L10.065 168.992C10.4961 168.647 11.1254 168.716 11.4707 169.147C11.816 169.578 11.7465 170.208 11.3154 170.553L5.07159 175.555L10.0731 181.798C10.4183 182.229 10.3488 182.859 9.91777 183.204C9.48673 183.549 8.85739 183.48 8.51211 183.049L2.88545 176.024ZM229.449 -16.1185C213.963 2.49666 193.29 19.7254 171.769 33.1983C150.255 46.6669 127.829 56.4222 108.82 60.0332C89.8894 63.6293 73.9325 61.1906 65.9618 49.6483C61.9916 43.899 60.1123 36.0225 60.7461 25.8241C61.3797 15.6284 64.5275 3.04751 70.6855 -12.1984L72.5399 -11.4494C66.4292 3.67959 63.358 16.0406 62.7423 25.9481C62.1267 35.8529 63.9678 43.241 67.6076 48.5118C74.857 59.0097 89.6789 61.6335 108.446 58.0684C127.135 54.5183 149.326 44.8888 170.708 31.5031C192.082 18.1218 212.584 1.02664 227.912 -17.3976L229.449 -16.1185ZM70.6855 -12.1984C78.5221 -31.6003 87.6669 -42.9643 97.2759 -47.8098C106.975 -52.7005 116.926 -50.848 126.011 -44.3649C135.061 -37.9069 143.324 -26.8219 149.888 -13.0286C156.459 0.779692 161.361 17.3745 163.644 34.9373C168.207 70.0401 162.321 109.184 138.131 137.691C113.905 166.241 71.513 183.901 3.55612 176.393L3.77574 174.405C71.2963 181.865 112.938 164.289 136.606 136.397C160.311 108.462 166.179 69.9554 161.661 35.1951C159.403 17.8263 154.557 1.43627 148.082 -12.1692C141.601 -25.7897 133.523 -36.5475 124.849 -42.7369C116.21 -48.9013 107.055 -50.5009 98.1764 -46.024C89.2086 -41.5018 80.3068 -30.6786 72.5399 -11.4494L70.6855 -12.1984Z'
        fill='white'
      />
    </g>
    <defs>
      <clipPath id='clip0_1323_37752'>
        <rect
          width='251'
          height='234.168'
          fill='white'
          transform='translate(234.168 -57) rotate(90)'
        />
      </clipPath>
    </defs>
  </svg>
)

const DoodleOutline = (props) => (
  <svg
    width='399'
    height='370'
    viewBox='0 0 399 370'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    {...props}
  >
    <path
      d='M199.574 132.655C280.166 167.437 463.347 341.141 280.981 310.666C-97.3251 247.447 -216.226 -134.853 273.428 156.521'
      stroke='white'
      strokeWidth='2'
      strokeLinecap='round'
    />
  </svg>
)

export default FeaturedPage
